import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    SortDirectionEnterprise,
    SortDirectionFinancing
} from '@lsq-frontend/angular-shared/api-service';
import {
    EnrollmentStatus,
    ListSupplierPaymentMethodsResponse,
    ListSupplierPaymentMethodsService,
    SetSupplierPrimaryContactRequest,
    UpdateSupplierPrimaryContactRequest
} from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import { DashboardClaim, SortDirection } from '@lsq-frontend/remote-services/enterprise/common';
import { DateFormat, forLifeOf, LifecycleAware, Modal, Regex } from '@lsq-frontend/common-ui';
import { ClaimService, FeatureFlagService } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map, startWith } from 'rxjs/operators';
import {
    ResetEnrollmentRequest,
    SaveResetEnrollmentService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/supplier/reset-enrollment/save-reset-enrollment';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { TAKE_ALL } from '../../../../../supply-chain/constants';
import { SendSupplierEnrollmentEmailModalComponent } from '../../../modals/send-supplier-enrollment-email-modal.component';
import { SupplierPrimaryContactModalComponent } from '../../../modals/supplier-primary-contact-modal.component';
import { SupplierResetEnrollmenttModalComponent } from '../../../modals/supplier-reset-enrollment-modal.componen';
import { ManageSupplierService } from '../../manage-supplier.service';

@LifecycleAware()
@Component({
    selector: 'supplier-implementation-enrollment-invite',
    styleUrls: ['./supplier-implementation-enrollment-invite.component.scss'],
    template: `
        <card [headerTitle]="'Enrollment'">
            <div card-body class="implementation-card-body">
                <!-- No previous invite sent out, and has no primary contact email -->
                <div *ngIf="!(latestEnrollment$ | async) && !(hasPrimaryContactEmail$ | async)">
                    <lsq-label
                        label="Enrollment Link Email"
                        [subLabel]="
                            'You must first designate a primary contact before sending the enrollment
                            email.'
                        "
                    ></lsq-label>
                    <data-card id="primaryContactCard" [style.marginBottom]="'2rem'">
                        <p class="label">Primary Contact</p>
                        <div class="primary-contact" *ngIf="this.hasPartialPrimaryContact$ | async">
                            <p class="body">
                                {{ (supplierDetails$ | async).primaryContactFirstName || '–' }}
                                {{ (supplierDetails$ | async).primaryContactLastName }}
                            </p>
                            <p class="body">
                                {{ (supplierDetails$ | async).primaryContactEmailAddress }}
                            </p>
                        </div>
                        <div
                            *ngIf="!(this.hasPartialPrimaryContact$ | async)"
                            class="primary-contact"
                        >
                            None
                        </div>
                        <lsq-button
                            appearance="transparent"
                            [disabled]="!(canEditSuppliers$ | async)"
                            (buttonClick)="handlePrimaryContactClick()"
                        >
                            {{ (this.hasPartialPrimaryContact$ | async) ? 'Edit' : 'Add Contact' }}
                        </lsq-button>
                    </data-card>
                </div>

                <div *ngIf="(latestEnrollmentStatus$ | async) === enrollmentStatus.INVITED">
                    <lsq-label
                        label="Enrollment Link Email"
                        [subLabel]="
                            'An invite has been sent to this contact on ' +
                            ((latestEnrollment$ | async).createdAt | lsqDate: 'shortDate') +
                            ' at ' +
                            ((latestEnrollment$ | async).createdAt | lsqDate: dateFormat.ShortTime)
                        "
                    ></lsq-label>
                    <data-card id="enrollmentContactCard" [style.marginBottom]="'2rem'">
                        <p class="label">Enrollment Contact</p>
                        <div class="primary-contact">
                            <p class="body">
                                {{ (latestEnrollment$ | async).enrollmentContactFirstName || '–' }}
                                {{ (latestEnrollment$ | async).enrollmentContactLastName }}
                            </p>
                            <p class="body">
                                {{ (latestEnrollment$ | async).enrollmentContactEmail || '–' }}
                            </p>
                        </div>
                    </data-card>

                    <div class="divider"></div>
                </div>

                <div *ngIf="(latestEnrollmentStatus$ | async) === enrollmentStatus.ENROLLED">
                    <lsq-label
                        label="Enrollment Complete"
                        [subLabel]="'The following contact has completed enrollment'"
                    ></lsq-label>
                    <data-card id="enrollmentContactCard" [style.marginBottom]="'2rem'">
                        <p class="label">Enrollment Contact</p>
                        <div class="primary-contact">
                            <p class="body">
                                {{ (latestEnrollment$ | async).enrollmentContactFirstName || '–' }}
                                {{ (latestEnrollment$ | async).enrollmentContactLastName }}
                            </p>
                            <p class="body">
                                {{ (latestEnrollment$ | async).enrollmentContactEmail || '–' }}
                            </p>
                        </div>
                    </data-card>
                </div>
                <div
                    class="enrollment-in-progress"
                    *ngIf="
                        (latestEnrollmentStatus$ | async) === enrollmentStatus.USER_CREATED &&
                        paymentMethods
                    "
                >
                    <lsq-label
                        class="in-progress-label"
                        label="Enrollment In Progress"
                        [subLabel]="'The following contact has started enrollment'"
                    ></lsq-label>
                    <lsq-button
                        *featureFlag="featureFlags.SCFReinviteMidEnroll"
                        appearance="transparent"
                        id="re-send-invite"
                        [disabled]="
                            notificationEmailsErrorMessage ||
                            !(hasPrimaryContactEmail$ | async) ||
                            !(canEditSuppliers$ | async)
                        "
                        (buttonClick)="sendEmail()"
                    >
                        Re-invite
                    </lsq-button>
                    <data-card id="enrollmentContactCard" [style.marginBottom]="'2rem'">
                        <p class="label">Enrollment Contact</p>
                        <div class="primary-contact">
                            <p class="body">
                                {{ (latestEnrollment$ | async).enrollmentContactFirstName || '–' }}
                                {{ (latestEnrollment$ | async).enrollmentContactLastName }}
                            </p>
                            <p class="body">
                                {{ (latestEnrollment$ | async).enrollmentContactEmail || '–' }}
                            </p>
                        </div>
                        <lsq-button
                            *ngIf="paymentMethods.totalCount === 0 && resetEnrollmentIsOn$ | async"
                            appearance="transparent"
                            [disabled]="!(canEditSuppliers$ | async)"
                            (buttonClick)="handleResetEnrollment()"
                        >
                            Edit
                        </lsq-button>
                    </data-card>
                </div>

                <div
                    *ngIf="
                        (latestEnrollmentStatus$ | async) !== enrollmentStatus.USER_CREATED &&
                        (latestEnrollmentStatus$ | async) !== enrollmentStatus.ENROLLED &&
                        (hasPrimaryContactEmail$ | async)
                    "
                >
                    <lsq-label
                        label="Primary Contact"
                        [subLabel]="'This person is the current primary contact for the supplier'"
                    ></lsq-label>
                    <data-card id="primaryContactCard" [style.marginBottom]="'2rem'">
                        <p class="label">Primary Contact</p>
                        <div class="primary-contact">
                            <p class="body">
                                {{ (supplierDetails$ | async).primaryContactFirstName || '–' }}
                                {{ (supplierDetails$ | async).primaryContactLastName }}
                            </p>
                            <p class="body">
                                {{ (supplierDetails$ | async).primaryContactEmailAddress }}
                            </p>
                        </div>
                        <lsq-button
                            appearance="transparent"
                            [disabled]="!(canEditSuppliers$ | async)"
                            (buttonClick)="handlePrimaryContactClick()"
                        >
                            Edit
                        </lsq-button>
                    </data-card>

                    <tag-input
                        [style.marginBottom]="'3rem'"
                        [(ngModel)]="notificationEmails"
                        placeholder="john@company.com"
                        (ngModelChange)="validateEmails()"
                        label="Enrollment Notifications"
                        subLabel="Send notifications to other emails when the enrollment link has been sent out to this supplier."
                        [tagIndexesWithError]="notificationEmailsWithError"
                        [errorMessage]="notificationEmailsErrorMessage"
                    ></tag-input>

                    <p *ngIf="!!(latestEnrollment$ | async)" [style.marginBottom]="'1rem'">
                        <b>Note:</b> Sending a new invite will invalidate invite links in any
                        previously sent out emails.
                    </p>
                    <lsq-button
                        appearance="primary"
                        id="send-invite-primary-contact"
                        [disabled]="
                            notificationEmailsErrorMessage ||
                            !(hasPrimaryContactEmail$ | async) ||
                            !(canEditSuppliers$ | async)
                        "
                        (buttonClick)="sendEmail()"
                    >
                        Send Invite to Primary Contact
                    </lsq-button>
                </div>
            </div>
        </card>
    `
})
export class ManageSupplierImplementationEnrollmentInviteComponent {
    public supplierDetails$ = this._manageSupplierService.supplierDetails$;
    public supplierRelationship$ = this._manageSupplierService.currentSupplierRelationship$;
    public readonly enrollmentStatus = EnrollmentStatus;
    public readonly dateFormat = DateFormat;

    public featureFlags = FeatureFlags;

    public paymentMethods: ListSupplierPaymentMethodsResponse;

    public userCreated$ = this.supplierRelationship$.pipe(
        filter((relationship) => !!relationship),
        map((relationship) =>
            relationship.supplierEnrollments?.some(
                (enrollment) => enrollment.enrollmentStatus === EnrollmentStatus.USER_CREATED
            )
        )
    );

    public resetEnrollmentIsOn$ = this._featureFlagService.isOn(FeatureFlags.ResetEnrollment);

    public latestEnrollment$ = this.supplierRelationship$.pipe(
        filter((relationship) => !!relationship),
        map(
            (relationship) =>
                relationship.supplierEnrollments
                    ?.filter(
                        (enrollment) => enrollment.enrollmentStatus !== EnrollmentStatus.TRANSFERRED
                    )
                    .sort((a, b) =>
                        a.createdAt < b.createdAt ? 1 : a.createdAt === b.createdAt ? 0 : -1
                    )[0]
        )
    );

    public latestEnrollmentStatus$ = this.latestEnrollment$.pipe(
        filter((enrollment) => !!enrollment),
        map((enrollment) => enrollment.enrollmentStatus)
    );

    public notificationEmails: string[] = [];
    public notificationEmailsWithError: number[] = [];
    public notificationEmailsErrorMessage: string;

    public hasPrimaryContactEmail$: Observable<boolean> = this.supplierDetails$.pipe(
        map((supplierDetails) => !!supplierDetails?.primaryContactEmailAddress)
    );

    public canEditSuppliers$ = this._claimService.isOn(DashboardClaim.AdminEditSupplier);

    constructor(
        private _manageSupplierService: ManageSupplierService,
        private _modalService: ModalService,
        private _claimService: ClaimService,
        private _resetEnrollmentService: SaveResetEnrollmentService,
        private _listSupplierPaymentMethodsService: ListSupplierPaymentMethodsService,
        private _featureFlagService: FeatureFlagService
    ) {
        this._manageSupplierService.currentSupplierRelationship$
            .pipe(
                forLifeOf(this),
                filter((relationship) => !!relationship)
            )
            .subscribe(async (relationship) => {
                this.paymentMethods = await this._listSupplierPaymentMethodsService.executeOnce({
                    supplierKey: relationship.supplierKey,
                    take: TAKE_ALL,
                    skip: 0,
                    sortColumn: 'default',
                    sortDirection: SortDirectionEnterprise.Desc
                });
            });
    }

    public async sendEmail(): Promise<void> {
        const supplierDetails = await this.supplierDetails$.pipe(first()).toPromise();
        const relationship = await this._manageSupplierService.currentSupplierRelationship$
            .pipe(first())
            .toPromise();

        const sendingEmailSubject = new BehaviorSubject(false);

        this._modalService.showModal(
            new Modal({
                title: 'Send Enrollment Email',
                bodyComponent: SendSupplierEnrollmentEmailModalComponent,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Confirm And Send',
                showCancelButton: true,
                data: {
                    notificationEmails: this.notificationEmails,
                    supplierDetails,
                    supplierName: relationship.supplierPreferredName
                },
                isSubmitting$: sendingEmailSubject.asObservable(),
                onOkClick: async () => {
                    sendingEmailSubject.next(true);
                    await this._manageSupplierService.sendEnrollmentEmail(
                        relationship.relationshipKey,
                        this.notificationEmails
                    );
                    this._modalService.closeModal();
                    this._manageSupplierService.loadRelationship(relationship.relationshipKey);
                    sendingEmailSubject.next(false);
                }
            })
        );
    }

    public validateEmails(): void {
        this.notificationEmailsWithError = [];
        const lowerCaseEmails = this.notificationEmails.map((email) => email.toLowerCase());

        // Email character length error takes precedence over format error.
        lowerCaseEmails.forEach((email, index) => {
            if (!Regex.Email.test(email)) {
                this.notificationEmailsWithError.push(index);
                this.notificationEmailsErrorMessage =
                    'Please make sure all emails are valid email addresses.';
            }
            if (email.length > 50) {
                if (!this.notificationEmailsWithError.includes(index)) {
                    this.notificationEmailsWithError.push(index);
                }
                this.notificationEmailsErrorMessage =
                    'Email addresses must be less than 50 characters long.';
            }
        });

        const duplicates = lowerCaseEmails.filter(
            (email, index) => lowerCaseEmails.indexOf(email) != index
        );

        if (duplicates.length > 0) {
            duplicates.forEach((duplicate) => {
                this.notificationEmailsWithError.push(lowerCaseEmails.lastIndexOf(duplicate));
            });

            this.notificationEmailsErrorMessage = 'Can not have duplicated email addresses.';
        }

        if (this.notificationEmailsWithError.length === 0) {
            this.notificationEmailsErrorMessage = null;
        }

        // Having to many emails error takes precedence over all other errors.
        if (this.notificationEmails.length > 20) {
            this.notificationEmailsErrorMessage = 'Can not have more than 20 email addresses.';
        }
    }

    public async handlePrimaryContactClick(): Promise<void> {
        const supplierDetails = await this.supplierDetails$.pipe(first()).toPromise();

        const formGroup: FormGroup = new FormGroup({
            primaryContactFirstName: new FormControl('', [
                Validators.required,
                LsqValidators.maxLength(50)
            ]),
            primaryContactLastName: new FormControl('', [
                Validators.required,
                LsqValidators.maxLength(50)
            ]),
            primaryContactEmailAddress: new FormControl('', [
                Validators.required,
                LsqValidators.email
            ])
        });

        formGroup.reset({
            primaryContactFirstName: supplierDetails.primaryContactFirstName,
            primaryContactLastName: supplierDetails.primaryContactLastName,
            primaryContactEmailAddress: supplierDetails.primaryContactEmailAddress
        });

        const update =
            !!supplierDetails.primaryContactFirstName ||
            !!supplierDetails.primaryContactLastName ||
            !!supplierDetails.primaryContactEmailAddress;

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const okButtonIsDisabled$ = formGroup.valueChanges.pipe(
            map((_) => !formGroup.valid),
            startWith(!formGroup.valid)
        );

        this._modalService.showModal(
            new Modal({
                title: 'Enrollment Contact',
                bodyComponent: SupplierPrimaryContactModalComponent,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Save Contact',
                okButtonIsDisabled$,
                showCancelButton: true,
                data: formGroup,
                isSubmitting$: isSubmittingSubject,
                onOkClick: async () => {
                    if (formGroup.valid) {
                        isSubmittingSubject.next(true);
                        if (update) {
                            const request = new UpdateSupplierPrimaryContactRequest({
                                supplierKey: supplierDetails.supplierKey,
                                refCompanyKey: supplierDetails.supplierCompanyKey,
                                firstName: formGroup.controls.primaryContactFirstName.value,
                                lastName: formGroup.controls.primaryContactLastName.value,
                                emailAddress: formGroup.controls.primaryContactEmailAddress.value
                            });
                            await this._manageSupplierService.updateSupplierPrimaryContactService(
                                request
                            );
                        } else {
                            const request = new SetSupplierPrimaryContactRequest({
                                supplierKey: supplierDetails.supplierKey,
                                refCompanyKey: supplierDetails.supplierCompanyKey,
                                firstName: formGroup.controls.primaryContactFirstName.value,
                                lastName: formGroup.controls.primaryContactLastName.value,
                                emailAddress: formGroup.controls.primaryContactEmailAddress.value
                            });
                            await this._manageSupplierService.setSupplierPrimaryContactService(
                                request
                            );
                        }
                        this._manageSupplierService.loadSupplierDetails(
                            supplierDetails.supplierKey
                        );
                        isSubmittingSubject.next(false);
                        this._modalService.closeModal();
                    }
                },
                onCancelClick: () => this._modalService.closeModal()
            })
        );
    }

    public async handleResetEnrollment(): Promise<void> {
        const supplierDetails = await this.supplierDetails$.pipe(first()).toPromise();

        const formGroup: FormGroup = new FormGroup({
            primaryContactFirstName: new FormControl('', [
                Validators.required,
                LsqValidators.maxLength(50)
            ]),
            primaryContactLastName: new FormControl('', [
                Validators.required,
                LsqValidators.maxLength(50)
            ]),
            primaryContactEmailAddress: new FormControl('', [
                Validators.required,
                LsqValidators.email
            ])
        });

        formGroup.reset({
            primaryContactFirstName: supplierDetails.primaryContactFirstName,
            primaryContactLastName: supplierDetails.primaryContactLastName,
            primaryContactEmailAddress: supplierDetails.primaryContactEmailAddress
        });

        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const okButtonIsDisabled$ = formGroup.valueChanges.pipe(
            map((_) => !formGroup.valid),
            startWith(!formGroup.valid)
        );

        this._modalService.showModal(
            new Modal({
                title: 'Enrollment Contact',
                bodyComponent: SupplierResetEnrollmenttModalComponent,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Save Contact',
                okButtonIsDisabled$,
                showCancelButton: true,
                data: formGroup,
                isSubmitting$: isSubmittingSubject,
                onOkClick: async () => {
                    if (formGroup.valid) {
                        isSubmittingSubject.next(true);
                        const request = new UpdateSupplierPrimaryContactRequest({
                            supplierKey: supplierDetails.supplierKey,
                            refCompanyKey: supplierDetails.supplierCompanyKey,
                            firstName: formGroup.controls.primaryContactFirstName.value,
                            lastName: formGroup.controls.primaryContactLastName.value,
                            emailAddress: formGroup.controls.primaryContactEmailAddress.value
                        });
                        await this._manageSupplierService.updateSupplierPrimaryContactService(
                            request
                        );

                        const relationship = await this._manageSupplierService.currentSupplierRelationship$
                            .pipe(first())
                            .toPromise();
                        const resetEnrollmentRequest = new ResetEnrollmentRequest({
                            relationshipKey: relationship.relationshipKey,
                            companyKey: supplierDetails.supplierCompanyKey
                        });

                        await this._resetEnrollmentService.executeOnce(resetEnrollmentRequest);

                        await this._manageSupplierService.loadRelationship(
                            relationship.relationshipKey
                        );
                        isSubmittingSubject.next(false);
                        this._modalService.closeModal();
                    }
                },
                onCancelClick: () => this._modalService.closeModal()
            })
        );
    }
}
